/**
 * R1 mocked handlers encoding the FROZEN GL-1 §2 (Card) + §4 (DueCard/recall)
 * DTOs — the MSW-equivalent so GL-4b/5b build + screenshot against the CONTRACT
 * with no dependency on unmerged server branches. Enabled by REACT_APP_GL_MOCK.
 *
 * CONCURRENCY TOKEN (per the arch owner's GL-1 §2 delta): the Card carries a
 * discrete monotonic integer `version`. The expected-version + 409 edit flow
 * (P13 monotonicity) keys off `version` — the client sends the card's known
 * `version` as `expectedVersion`; a mismatch is 409. `updatedAt` remains a
 * plain last-modified timestamp and is NOT the token.
 *
 * In-memory ONLY (P12): no localStorage/sessionStorage/IndexedDB. `glMockReset`
 * re-seeds for tests.
 */
export const glMockEnabled = () => process.env.REACT_APP_GL_MOCK === "true";

const BASE_TS = "2026-07-11T12:00:00.000Z";
let rev = 0;
const nextUpdatedAt = () => {
  rev += 1;
  return new Date(Date.parse(BASE_TS) + rev * 1000).toISOString();
};

// SM-2-lite (§4): 1 -> 3 -> 7 -> 14 -> 30 on correct; reset to 1 on incorrect;
// skipped leaves the interval unchanged.
const LADDER = [1, 3, 7, 14, 30];
const nextInterval = (current, outcome) => {
  if (outcome === "incorrect") {
    return 1;
  }
  if (outcome === "skipped") {
    return current;
  }
  const idx = LADDER.indexOf(current);
  if (idx === -1) {
    return LADDER[0];
  }
  return LADDER[Math.min(idx + 1, LADDER.length - 1)];
};
const dueAfter = (days) =>
  new Date(Date.parse(BASE_TS) + days * 86400000).toISOString();

let store;
const seed = () => ({
  drafts: [
    {
      cardId: "card-d1",
      userId: "u-mock",
      questionId: "q-1",
      explanationVersion: "exp-7",
      cardKind: "personalized",
      whyExcerpt: "I thought the raised lactate meant pure hypovolaemia.",
      misconceptionSummary: "Conflates a raised lactate with volume status alone.",
      front: "In early sepsis, what does a rising lactate most directly reflect?",
      back: "Tissue hypoperfusion / anaerobic metabolism — not volume status alone.",
      sourceRefs: [{ kind: "explanation", ref: "exp-7" }],
      createdFrom: { gradeEventId: "ge-1", hintTrail: [0], gradedCorrect: false },
      state: "draft",
      editedByStudent: false,
      deck: null,
      version: 1,
      createdAt: BASE_TS,
      updatedAt: BASE_TS
    },
    {
      cardId: "card-d2",
      userId: "u-mock",
      questionId: "q-1",
      explanationVersion: "exp-7",
      cardKind: "generic",
      whyExcerpt: null,
      misconceptionSummary: null,
      front: "First-line resuscitation fluid in undifferentiated septic shock?",
      back: "Balanced crystalloid, ~30 mL/kg, reassessed against dynamic markers.",
      sourceRefs: [{ kind: "keyPoint", ref: "kp-3" }],
      createdFrom: { gradeEventId: "ge-1", hintTrail: [], gradedCorrect: false },
      state: "draft",
      editedByStudent: false,
      deck: null,
      version: 1,
      createdAt: BASE_TS,
      updatedAt: BASE_TS
    }
  ],
  due: [
    {
      cardId: "card-r1",
      questionId: "q-9",
      front: "Antidote for methotrexate toxicity?",
      back: "Folinic acid (leucovorin).",
      audioEligible: true,
      due: dueAfter(0),
      interval: 3,
      reviews: 2
    },
    {
      cardId: "card-r2",
      questionId: "q-12",
      front: "Earliest ECG change in hyperkalaemia?",
      back: "Peaked T waves.",
      audioEligible: false,
      due: dueAfter(0),
      interval: 7,
      reviews: 4
    }
  ],
  total: 5
});

export const glMockReset = () => {
  rev = 0;
  store = seed();
};
const ensure = () => {
  if (!store) {
    glMockReset();
  }
  return store;
};

const ok = (data) => ({ status: 200, data });
const err = (status, data = null) => ({ status, data });
// Return SNAPSHOTS, not store references — a real API serializes its response, so
// a caller's held copy must not mutate when the store later changes (and tests
// capturing a pre-edit version must stay pre-edit).
const clone = (card) => (card ? JSON.parse(JSON.stringify(card)) : card);

const parsePath = (path) => {
  const [p, query = ""] = String(path).split("?");
  const params = {};
  query.split("&").filter(Boolean).forEach((kv) => {
    const [k, v] = kv.split("=");
    params[decodeURIComponent(k)] = decodeURIComponent(v || "");
  });
  return { p, params };
};

/**
 * @returns {{status:number, data:*}|null} null when `path` is not a GL route
 *   (so gl-fetch falls through to the real network).
 */
export const glMockHandle = (method, path, body) => {
  const db = ensure();
  const { p, params } = parsePath(path);
  const M = String(method || "GET").toUpperCase();

  // ---- mount gate (wire-contract row 10): the mock caller is always served ----
  if (p === "/gl/status" && M === "GET") {
    return ok({ allowed: true });
  }

  // ---- card draft + list + disposition (wire-contract rows 2-6: BARE DTOs) ----
  if (p === "/gl/card/draft" && M === "POST") {
    const { questionId, gradeEventId, whyState, why } = body || {};
    if (!questionId || !gradeEventId) {
      return err(503, { glError: "unavailable" });
    }
    const adequate = whyState === "adequate";
    const card = {
      cardId: `card-m${db.drafts.length + 1}`,
      userId: "u-mock",
      questionId,
      explanationVersion: "exp-7",
      cardKind: adequate ? "personalized" : "generic",
      whyExcerpt: adequate ? String(why || "").slice(0, 280) : null,
      misconceptionSummary: adequate ? "Mock misconception summary." : null,
      front: "Mock front",
      back: "Mock back",
      sourceRefs: [{ kind: "explanation", ref: "exp-7" }],
      createdFrom: { gradeEventId, hintTrail: [], gradedCorrect: false },
      state: "draft",
      editedByStudent: false,
      deck: null,
      version: 1,
      createdAt: nextUpdatedAt(),
      updatedAt: nextUpdatedAt()
    };
    db.drafts.push(card);
    return ok(clone(card));
  }

  if (p === "/gl/card/drafts" && M === "GET") {
    // BARE array (contract row 3) — no {cards} wrapper.
    return ok(db.drafts.filter((c) => c.state === "draft").map(clone));
  }

  const cardMatch = p.match(/^\/gl\/card\/([^/]+)(?:\/(accept|discard|edit))?$/);
  if (cardMatch) {
    const [, cardId, action] = cardMatch;
    const card = db.drafts.find((c) => c.cardId === cardId);
    if (!card) {
      return err(404, { glError: "not_found" });
    }
    if (M === "POST" && action === "accept") {
      card.state = "accepted";
      card.version += 1;
      card.updatedAt = nextUpdatedAt();
      card.deck = { due: dueAfter(1), interval: 1, ease: 2.5, reviews: 0 };
      return ok(clone(card));
    }
    if (M === "POST" && action === "discard") {
      card.state = "discarded";
      card.version += 1;
      card.updatedAt = nextUpdatedAt();
      return ok(clone(card));
    }
    if (M === "POST" && action === "edit") {
      // Contract row 5: POST /gl/card/{id}/edit, optimistic concurrency on the
      // discrete `version` field (P13). Stale token -> 409 typed conflict.
      const expected = body && body.version;
      if (expected !== card.version) {
        return err(409, { glError: "conflict" });
      }
      if (body.front !== undefined) card.front = body.front;
      if (body.back !== undefined) card.back = body.back;
      card.editedByStudent = true;
      card.version += 1;
      card.updatedAt = nextUpdatedAt();
      return ok(clone(card));
    }
  }

  // Test/dev hook: simulate a concurrent server-side edit (bumps `version`) so a
  // held client edit goes stale -> 409 on save. Not a product endpoint.
  const bump = p.match(/^\/gl\/card\/([^/]+)\/__bump$/);
  if (bump && M === "POST") {
    const card = db.drafts.find((c) => c.cardId === bump[1]);
    if (card) {
      card.version += 1;
      card.updatedAt = nextUpdatedAt();
      return ok(clone(card));
    }
    return err(404, { glError: "not_found" });
  }

  // ---- GL-5b due queue (§4 verbatim) ----
  if (p === "/gl/deck/due" && M === "GET") {
    return ok({
      due: db.due.map((d) => ({ ...d })),
      counts: { dueToday: db.due.length, total: db.total }
    });
  }
  if (p === "/gl/deck/recall" && M === "POST") {
    const { cardId, outcome } = body || {};
    const idx = db.due.findIndex((d) => d.cardId === cardId);
    if (idx === -1) {
      return err(404);
    }
    const card = db.due[idx];
    const interval = nextInterval(card.interval, outcome);
    card.interval = interval;
    card.reviews += 1;
    card.due = dueAfter(interval);
    // correct/incorrect leave the queue for this session; skipped stays due.
    if (outcome !== "skipped") {
      db.due.splice(idx, 1);
    }
    // Contract row 8: the BARE rescheduled DueCardDto — no counts wrapper
    // (clients refresh counts via /gl/deck/due, row 7).
    return ok({ ...card });
  }

  return null;
};
